import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../services/api';
import './Settings.css';

function CustomerPlatformFeeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    type: 'fixed',
    fixedAmount: 5,
    percentage: 2,
    minFee: 2,
    maxFee: 50
  });
  // GST rate for platform fee - fetched from backend Settings.taxRates.platformGst
  // NOTE: This MUST match backend centralized FinancialService rates
  const [platformGstRate, setPlatformGstRate] = useState(18); // Default 18%, fetched from backend
  const [originalSettings, setOriginalSettings] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fee preview state
  const [previewSubtotal, setPreviewSubtotal] = useState(500);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getCustomerPlatformFeeSettings();
      const data = response.data.data.customerPlatformFee;
      setSettings({
        enabled: data.enabled,
        type: data.type,
        fixedAmount: data.fixedAmount,
        percentage: data.percentage,
        minFee: data.minFee,
        maxFee: data.maxFee
      });
      setOriginalSettings({
        enabled: data.enabled,
        type: data.type,
        fixedAmount: data.fixedAmount,
        percentage: data.percentage,
        minFee: data.minFee,
        maxFee: data.maxFee
      });
      // Fetch GST rate from tax rates in response
      if (response.data.data.taxRates?.platformGst) {
        setPlatformGstRate(response.data.data.taxRates.platformGst);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : parseFloat(value)) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateCustomerPlatformFeeSettings(settings);
      setOriginalSettings({ ...settings });
      setMessage({ type: 'success', text: 'Platform fee settings updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings({ ...originalSettings });
    }
  };

  const hasChanges = () => {
    if (!originalSettings) return false;
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  // Calculate preview fee
  const calculatePreviewFee = () => {
    if (!settings.enabled) return 0;

    if (settings.type === 'fixed') {
      return settings.fixedAmount;
    } else {
      let fee = (previewSubtotal * settings.percentage) / 100;
      // Apply min/max caps
      if (settings.minFee && fee < settings.minFee) fee = settings.minFee;
      if (settings.maxFee && fee > settings.maxFee) fee = settings.maxFee;
      return Math.round(fee * 100) / 100;
    }
  };

  // Calculate GST on platform fee using rate from backend
  // NOTE: This is for UI preview only - actual calculation happens in backend FinancialService
  const calculateGst = (fee) => {
    return Math.round(fee * (platformGstRate / 100) * 100) / 100;
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  const previewFee = calculatePreviewFee();
  const previewGst = calculateGst(previewFee);

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1>Platform Fee Settings</h1>
          <p className="subtitle">Configure the platform fee charged to customers on each order</p>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* Info Card */}
      <div className="info-card">
        <h3>How Platform Fee Works</h3>
        <p>
          Platform fee is charged to customers on each order to cover operational costs.
          This fee is subject to {platformGstRate}% GST (SAC Code: 998319 - Online Intermediary Services).
        </p>
        <ul>
          <li><strong>Fixed Mode:</strong> A flat fee is charged regardless of order value</li>
          <li><strong>Percentage Mode:</strong> Fee is calculated as a percentage of subtotal, with min/max caps</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Enable/Disable Toggle */}
        <div className="form-section">
          <h3>Platform Fee Status</h3>
          <div className="toggle-container">
            <label className="toggle-label">
              <input
                type="checkbox"
                name="enabled"
                checked={settings.enabled}
                onChange={handleInputChange}
              />
              <span className="toggle-switch"></span>
              <span className="toggle-text">
                {settings.enabled ? 'Platform Fee Enabled' : 'Platform Fee Disabled'}
              </span>
            </label>
          </div>
        </div>

        {settings.enabled && (
          <>
            {/* Fee Type Selection */}
            <div className="form-section">
              <h3>Fee Type</h3>
              <div className="radio-group">
                <label className={`radio-card ${settings.type === 'fixed' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="fixed"
                    checked={settings.type === 'fixed'}
                    onChange={handleInputChange}
                  />
                  <div className="radio-content">
                    <span className="radio-title">Fixed Amount</span>
                    <span className="radio-description">Charge a flat fee on every order</span>
                  </div>
                </label>
                <label className={`radio-card ${settings.type === 'percentage' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="percentage"
                    checked={settings.type === 'percentage'}
                    onChange={handleInputChange}
                  />
                  <div className="radio-content">
                    <span className="radio-title">Percentage Based</span>
                    <span className="radio-description">Charge a percentage of order subtotal</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Fee Configuration */}
            <div className="form-section">
              <h3>Fee Configuration</h3>

              {settings.type === 'fixed' ? (
                <div className="form-group">
                  <label>Fixed Amount (Rs.)</label>
                  <input
                    type="number"
                    name="fixedAmount"
                    value={settings.fixedAmount}
                    onChange={handleInputChange}
                    min="0"
                    step="1"
                    required
                    className="form-input"
                  />
                  <small>The exact amount charged as platform fee on every order</small>
                </div>
              ) : (
                <div className="fee-inputs-grid">
                  <div className="form-group">
                    <label>Percentage (%)</label>
                    <input
                      type="number"
                      name="percentage"
                      value={settings.percentage}
                      onChange={handleInputChange}
                      min="0"
                      max="20"
                      step="0.5"
                      required
                      className="form-input"
                    />
                    <small>Percentage of order subtotal</small>
                  </div>

                  <div className="form-group">
                    <label>Minimum Fee (Rs.)</label>
                    <input
                      type="number"
                      name="minFee"
                      value={settings.minFee}
                      onChange={handleInputChange}
                      min="0"
                      step="1"
                      required
                      className="form-input"
                    />
                    <small>Floor for platform fee</small>
                  </div>

                  <div className="form-group">
                    <label>Maximum Fee (Rs.)</label>
                    <input
                      type="number"
                      name="maxFee"
                      value={settings.maxFee}
                      onChange={handleInputChange}
                      min="0"
                      step="1"
                      required
                      className="form-input"
                    />
                    <small>Cap for platform fee</small>
                  </div>
                </div>
              )}
            </div>

            {/* Tax Information */}
            <div className="form-section">
              <h3>Tax Information</h3>
              <div className="tax-info-grid">
                <div className="tax-info-item">
                  <span className="tax-label">GST Rate</span>
                  <span className="tax-value">{platformGstRate}%</span>
                </div>
                <div className="tax-info-item">
                  <span className="tax-label">CGST</span>
                  <span className="tax-value">{platformGstRate / 2}%</span>
                </div>
                <div className="tax-info-item">
                  <span className="tax-label">SGST</span>
                  <span className="tax-value">{platformGstRate / 2}%</span>
                </div>
                <div className="tax-info-item">
                  <span className="tax-label">SAC Code</span>
                  <span className="tax-value">998319</span>
                </div>
              </div>
              <small className="tax-note">GST is automatically calculated and added to the platform fee</small>
            </div>

            {/* Fee Preview */}
            <div className="form-section preview-section">
              <h3>Fee Preview Calculator</h3>
              <div className="preview-input-row">
                <div className="form-group">
                  <label>Order Subtotal (Rs.)</label>
                  <input
                    type="number"
                    value={previewSubtotal}
                    onChange={(e) => setPreviewSubtotal(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="10"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="preview-result">
                <div className="preview-row">
                  <span>Platform Fee:</span>
                  <span className="preview-value">Rs. {previewFee.toFixed(2)}</span>
                </div>
                <div className="preview-row">
                  <span>GST ({platformGstRate}%):</span>
                  <span className="preview-value">Rs. {previewGst.toFixed(2)}</span>
                </div>
                <div className="preview-row total">
                  <span>Total Platform Charges:</span>
                  <span className="preview-value">Rs. {(previewFee + previewGst).toFixed(2)}</span>
                </div>
                {settings.type === 'percentage' && (
                  <p className="preview-breakdown">
                    Calculation: {settings.percentage}% of Rs. {previewSubtotal} = Rs. {(previewSubtotal * settings.percentage / 100).toFixed(2)}
                    {previewFee !== (previewSubtotal * settings.percentage / 100) &&
                      ` (capped to Rs. ${previewFee.toFixed(2)})`}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={!hasChanges() || saving}
          >
            Reset Changes
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!hasChanges() || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CustomerPlatformFeeSettings;
